import type { FeatureConfig } from '@skynet/feature-config';
import { UngroupOutlined } from '@ant-design/icons';
import { EXCHANGE_DETAIL_ROUTE_PATH, EXCHANGES_LIST_URL, EXCHANGES_ROUTE_PATH } from './routes';

/**
 * Feature manifest for the admin Exchanges list and exchange detail pages. The
 * dashboard shell reads this single source of truth instead of hardcoding the route,
 * navigation entry, header label and Cerbos action across its routing,
 * navigation, header and access-control modules.
 *
 * `route.load` and `detailRoute.load` are dynamic-import thunks, so importing this config for its
 * nav/header metadata never pulls the page implementation into the main bundle.
 *
 * The route path, nav url and WS-login skip path come from this feature's own
 * `./routes`, which also supplies the URLs the `@skynet/sky-exchange`
 * components link to. The config never imports the sky-exchange barrel, which
 * would drag the Exchanges UI onto the boot path. `section`, `nav.key` and
 * `cerbosAction` stay literal: they are the identifier the Cerbos policy is
 * written against (`read:admin:exchanges`), and renaming any of them hides the
 * nav item until the policy is renamed too.
 *
 * `needsEntityId` is left at its default (`true`): the section also serves the
 * detail page declared in `detailRoute` (`/admin/exchanges/:exchangeId`).
 */
export const exchangesConfig = {
  domain: 'Admin',
  section: 'exchanges',
  route: {
    path: EXCHANGES_ROUTE_PATH,
    load: async () => ({ default: (await import('./Exchanges')).Exchanges }),
  },
  detailRoute: {
    path: EXCHANGE_DETAIL_ROUTE_PATH,
    load: async () => ({ default: (await import('./ExchangePage')).ExchangePage }),
  },
  nav: {
    key: 'admin.exchanges',
    label: 'Exchanges',
    icon: <UngroupOutlined />,
    url: EXCHANGES_LIST_URL,
  },
  header: { label: 'EXCHANGES' },
  cerbosAction: 'read:admin:exchanges',
  skipWsLoginPaths: [EXCHANGES_LIST_URL],
} as const satisfies FeatureConfig;
